import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import Counter from "@/components/common/Counter";
import { OrganCard, StateCard, JourneyCard } from "@/components/common/Cards";

export default function Home() {
  const [stats, setStats] = useState(null);
  const [organs, setOrgans] = useState([]);
  const [states, setStates] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/organs").then((r) => setOrgans(r.data));
    api.get("/states").then((r) => setStates(r.data));
    api.get("/journeys?limit=4").then((r) => setFeatured(r.data));
  }, []);

  return (
    <div>
      <Seo title="Transplant Story — Real stories. Real experiences." description="Explore transplant centers across India and hear directly from people who have lived through the journey." />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 mtj-rise">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-rust mb-6">Transplant Story</div>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05] text-ink">
              Real stories.<br />Real experiences.
            </h1>
            <p className="text-lg sm:text-xl text-neutral-600 mt-8 max-w-2xl leading-relaxed">
              Explore transplant centers across India and hear directly from people who have lived through the journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-10">
              <Link to="/share" data-testid="hero-share-cta" className="inline-flex justify-center items-center bg-ink text-white px-7 py-3.5 text-sm font-medium hover:bg-rust transition-colors">
                Share your journey
              </Link>
              <Link to="/explore" data-testid="hero-explore-cta" className="inline-flex justify-center items-center border border-neutral-300 text-ink px-7 py-3.5 text-sm font-medium hover:border-ink transition-colors">
                Explore journeys
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 mtj-rise" style={{ animationDelay: "0.15s" }}>
            <img
              src="https://static.prod-images.emergentagent.com/jobs/63227096-7576-4196-9731-b82d154cf8d0/images/6f2e4884e4bf6990e54d8c2bc913af1f199192c3747f4b89a27393e3e71e7931.jpeg"
              alt="A winding path rising toward the sun — every journey has a story"
              data-testid="hero-image"
              className="w-full max-w-md mx-auto lg:max-w-none rounded-2xl border border-neutral-200/70 shadow-[0_24px_60px_-30px_rgba(17,17,17,0.25)]"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* COUNTERS */}
      <section className="border-y border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          <Counter testId="counter-centres" value={stats?.centres} label="Registered transplant centers" tint={{ bg: "#EAF3F0", fg: "#3E7A66" }} />
          <Counter testId="counter-organs" value={stats?.organs} label="Major organ categories" tint={{ bg: "#F9EAEB", fg: "#B05A5A" }} />
          <Counter testId="counter-states" value={stats?.states} label="States & Union Territories" tint={{ bg: "#E9EFF7", fg: "#55799E" }} />
          <Counter testId="counter-journeys" value={stats?.journeys} label="Patient journeys shared" tint={{ bg: "#F5EDE1", fg: "#A0693A" }} />
        </div>
      </section>

      {/* EXPLORE BY ORGAN */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-2xl sm:text-3xl font-normal tracking-tight text-ink">Explore by organ</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {organs.map((o) => <OrganCard key={o.slug} organ={o} />)}
        </div>
      </section>

      {/* EXPLORE INDIA */}
      <section className="bg-white border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-8">
            <h2 className="text-2xl sm:text-3xl font-normal tracking-tight text-ink">Explore India</h2>
            <span className="text-[11px] font-mono text-neutral-400">{states.length} states & UTs</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {states.map((s) => <StateCard key={s.code} state={s} />)}
          </div>
        </div>
      </section>

      {/* FEATURED JOURNEYS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-2xl sm:text-3xl font-normal tracking-tight text-ink mb-8">Featured journeys</h2>
        {featured.length === 0 ? (
          <div className="border border-dashed border-neutral-300 p-10 text-center">
            <p className="text-neutral-600">No journeys have been published yet.</p>
            <p className="text-sm text-neutral-500 mt-2">The first stories are on their way. Would you like to share yours?</p>
            <Link to="/share" className="inline-block mt-6 text-sm font-medium text-rust hover:text-rust-hover">Share your journey →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featured.map((j) => <JourneyCard key={j.id} journey={j} />)}
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            ["01", "Explore", "Browse transplant centers across every Indian state and organ program."],
            ["02", "Listen", "Hear real voices and read real stories from recipients, donors and families."],
            ["03", "Share", "Add your own journey — by voice or in writing — in two to three minutes."],
          ].map(([n, t, d]) => (
            <div key={n}>
              <div className="font-mono text-rust text-sm">{n}</div>
              <div className="text-xl font-medium text-ink mt-3">{t}</div>
              <p className="text-neutral-600 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST STATEMENT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <p className="text-2xl sm:text-4xl font-light tracking-tight text-ink max-w-3xl mx-auto leading-tight">
          We don't tell you where to go. We help you understand what is there.
        </p>
        <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 mt-8 text-[11px] font-mono uppercase tracking-widest text-neutral-500">
          <span>Anonymous</span><span>Source-labelled</span><span>No rankings</span>
        </div>
      </section>
    </div>
  );
}
